import { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, FileText, PackageCheck, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatR, ORDER_STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OrderTimeline } from "@/components/OrderTimeline";
import { Reveal } from "@/components/Reveal";
import { BackButton } from "@/components/BackButton";

export default function OrderConfirmationPage() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const [email, setEmail] = useState(() => {
    if (location.state?.email) return location.state.email;
    try {
      return JSON.parse(localStorage.getItem("btm_last_order"))?.order_number === orderNumber
        ? JSON.parse(localStorage.getItem("btm_last_order"))?.email || ""
        : "";
    } catch {
      return "";
    }
  });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [searchParams] = useSearchParams();
  const [pfConfigured, setPfConfigured] = useState(false);
  const [pfMode, setPfMode] = useState("sandbox");
  const { user } = useAuth();
  const [stokvel, setStokvel] = useState(null);

  useEffect(() => {
    if (user) api.get("/stokvel/me").then((r) => setStokvel(r.data)).catch(() => {});
  }, [user]);

  const load = (em) => {
    api
      .get(`/orders/${orderNumber}`, { params: { email: em } })
      .then((r) => {
        setOrder(r.data.order);
        setError("");
      })
      .catch(() => setError("not-found"));
  };

  useEffect(() => {
    if (email) load(email);
    api.get("/payfast/config").then((r) => {
      setPfConfigured(r.data.configured);
      setPfMode(r.data.mode);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  // Returning from PayFast: poll until the ITN marks the order paid
  useEffect(() => {
    const pf = searchParams.get("payfast");
    if (!pf || !email) return;
    if (pf === "cancelled") {
      toast.error("Payment cancelled — you can try again below");
      return;
    }
    toast.info("Confirming your payment with PayFast…");
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      load(email);
      if (tries >= 10) clearInterval(t);
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, email]);

  const payWithPayfast = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${orderNumber}/payfast/initiate`, { email });
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.action;
      Object.entries(data.fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      toast.error(errMsg(e));
      setPaying(false);
    }
  };

  const mockPay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${orderNumber}/mock-pay`, { email });
      setOrder(data.order);
      toast.success("Payment verified — order confirmed");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPaying(false);
    }
  };

  const payWithStokvel = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${orderNumber}/stokvel-pay`, { email });
      setOrder(data.order);
      setStokvel((s) => (s ? { ...s, balance: Math.round((s.balance - (data.order?.total || 0)) * 100) / 100 } : s));
      toast.success("Paid from your Stokvel balance — order confirmed");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPaying(false);
    }
  };

  if (!email || error)
    return (
      <div className="max-w-md mx-auto px-4 py-24" data-testid="order-lookup">
        <div className="mb-4">
          <BackButton to="/shop" label="Back to shop" testId="order-lookup-back-button" />
        </div>
        <h1 className="font-display text-2xl font-bold text-navy">Find your order</h1>
        <p className="text-sm text-slate-500 mt-2">Enter the email used at checkout to view order {orderNumber}.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const em = emailInput.trim();
            setEmail(em);
            load(em);
          }}
          className="mt-6 flex gap-2"
        >
          <input
            type="email"
            required
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Email address"
            className="flex-1 h-12 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:ring-2 focus:ring-gold"
            data-testid="order-lookup-email"
          />
          <button className="h-12 px-5 rounded-xl bg-navy text-white text-sm font-bold" data-testid="order-lookup-submit">
            View
          </button>
        </form>
        {error && <p className="text-sm text-red-500 mt-3" data-testid="order-lookup-error">Order not found for that email.</p>}
      </div>
    );

  if (!order)
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-sm text-slate-500">Loading order…</div>;

  const paid = order.payment_status === "paid";

  const downloadInvoice = async () => {
    try {
      const r = await api.get(`/orders/${orderNumber}/invoice.pdf`, { params: { email }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `invoice-${orderNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="order-confirmation">
      <Reveal>
        <div className="bg-white rounded-3xl border border-slate-200/80 overflow-hidden">
          <div className="bg-navy text-white p-8 relative overflow-hidden">
            <div className="absolute inset-0 hero-grid-lines opacity-50" />
            <div className="relative flex items-start gap-4">
              <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${paid ? "bg-emerald-500/20 text-emerald-400" : "bg-gold/20 text-gold"}`}>
                {paid ? <PackageCheck size={22} /> : <CreditCard size={22} />}
              </span>
              <div className="flex-1">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-gold">
                  {paid ? "Order confirmed" : "Order received"}
                </p>
                <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1" data-testid="order-number">
                  {order.order_number}
                </h1>
                <p className="text-sm text-white/60 mt-1">
                  Status: {ORDER_STATUS_LABELS[order.status] || order.status} · Payment: {order.payment_status}
                </p>
              </div>
              <button
                onClick={downloadInvoice}
                className="shrink-0 h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center gap-1.5 transition-colors"
                data-testid="download-invoice-btn"
              >
                <FileText size={14} /> Invoice (PDF)
              </button>
            </div>
          </div>

          <div className="p-8">
            <OrderTimeline order={order} />

            {order.delivery_code && ["dispatched", "ready_for_collection"].includes(order.status) && (
              <div className="bg-navy rounded-2xl p-6 mb-7 text-center" data-testid="delivery-code-card">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">
                  {order.delivery_method === "delivery" ? "Your delivery code" : "Your collection code"}
                </p>
                <p className="font-mono text-4xl font-extrabold tracking-[0.3em] text-gold mt-2" data-testid="delivery-code">
                  {order.delivery_code}
                </p>
                <p className="text-xs text-white/60 mt-2">
                  {order.delivery_method === "delivery"
                    ? "Give this code to the driver when you receive your items — it confirms you received your order."
                    : "Show this code at the till when collecting — it confirms you received your order."}
                </p>
              </div>
            )}

            {!paid && (
              <div className="bg-cream border border-gold/30 rounded-2xl p-5 mb-7" data-testid="payment-pending-box">
                <p className="text-sm font-semibold text-navy">Complete your payment to confirm this order.</p>
                {user && stokvel?.activated && (
                  <div className="mt-3 rounded-2xl bg-navy text-white p-4" data-testid="stokvel-pay-box">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold">Stokvel balance</p>
                        <p className="font-mono font-extrabold text-lg" data-testid="stokvel-pay-balance">{formatR(stokvel.balance)}</p>
                      </div>
                      {stokvel.balance >= order.total && !stokvel.needs_reaccept ? (
                        <button onClick={payWithStokvel} disabled={paying}
                          className="h-11 px-6 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-white font-bold text-sm flex items-center gap-2 transition-colors"
                          data-testid="stokvel-pay-button">
                          <Wallet size={15} /> {paying ? "Processing…" : `Pay ${formatR(order.total)} with Stokvel`}
                        </button>
                      ) : (
                        <p className="text-[11px] text-white/60" data-testid="stokvel-pay-insufficient">
                          {stokvel.needs_reaccept ? "Accept the updated Stokvel terms in your profile to use your balance." : "Not enough Stokvel balance for this order — use PayFast below."}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
                  <ShieldCheck size={13} className="text-emerald-600" /> Secure payment by PayFast — cards, Instant EFT and more. No card details touch BTM servers.
                  {pfMode === "sandbox" && <span className="font-bold text-gold-hover">(Sandbox mode)</span>}
                </p>
                <button
                  onClick={payWithPayfast}
                  disabled={paying || !pfConfigured}
                  className="mt-4 h-11 px-6 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-white font-bold text-sm flex items-center gap-2 transition-colors gold-glow"
                  data-testid="payfast-pay-button"
                >
                  <CreditCard size={15} /> {paying ? "Redirecting to PayFast…" : "Pay securely with PayFast"}
                </button>
                <div className="mt-3">
                  <button
                    onClick={mockPay}
                    disabled={paying}
                    className="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors"
                    data-testid="mock-pay-button"
                  >
                    Testing without PayFast? Simulate a payment
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3" data-testid="order-items">
              {order.items.map((i) => (
                <div key={i.product_id} className="flex justify-between gap-4 text-sm">
                  <span className="text-slate-700">{i.qty} × {i.name}{i.variant && <span className="text-brand font-semibold"> — {i.variant}</span>}</span>
                  <span className="font-mono font-semibold text-navy shrink-0">{formatR(i.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100 space-y-2 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Subtotal</span><span className="font-mono">{formatR(order.subtotal)}</span>
              </div>
              {(order.promo_discount || 0) > 0 && (
                <div className="flex justify-between text-emerald-600" data-testid="order-promo-row">
                  <span>Promo code {order.promo_code}</span>
                  <span className="font-mono">-{formatR(order.promo_discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-500">
                <span>{order.delivery_method === "delivery" ? "Delivery" : "Collection"}</span>
                <span className="font-mono">{order.delivery_fee === 0 ? "FREE" : formatR(order.delivery_fee)}</span>
              </div>
              <div className="flex justify-between font-bold text-navy text-base">
                <span>Total</span><span className="font-mono" data-testid="order-total">{formatR(order.total)}</span>
              </div>
              <p className="text-[10px] text-slate-400 pt-1" data-testid="order-vat-note">
                BTMEDU VAT No: 4830303048 · This page with your invoice email serves as your tax invoice.
              </p>
            </div>

            {order.delivery_method === "collection" && order.collection_note && (
              <p className="mt-5 text-xs text-slate-500 bg-cream rounded-xl p-4" data-testid="collection-note">
                {order.collection_note}
              </p>
            )}
            {order.delivery_method === "delivery" && order.address && (
              <p className="mt-5 text-xs text-slate-500 bg-cream rounded-xl p-4" data-testid="delivery-address">
                Deliver to: {order.address.line1}{order.address.suburb ? `, ${order.address.suburb}` : ""}, {order.address.city}{order.address.province ? `, ${order.address.province}` : ""}, {order.address.postal_code}
              </p>
            )}

            <Link to="/shop" className="inline-flex mt-7 text-sm font-bold text-brand hover:text-brand-accent transition-colors" data-testid="order-continue-shopping">
              Continue shopping
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
